import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import FranchiseesPage from "@/pages/FranchiseesPage";
import FranchiseeDetailPage from "@/pages/FranchiseeDetailPage";
import ContractsPage from "@/pages/ContractsPage";
import ContractRenewalsPage from "@/pages/ContractRenewalsPage";
import FilesPage from "@/pages/FilesPage";
import ContactsPage from "@/pages/ContactsPage";
import FormIntakePage from "@/pages/FormIntakePage";
import PublicFolderSharePage from "@/pages/PublicFolderSharePage";
import PublicDBSForm from "@/pages/PublicDBSForm";
import PublicTerritorySharePage from "@/pages/PublicTerritorySharePage";
import PublicAgreedTerritoryPage from "@/pages/PublicAgreedTerritoryPage";
import PortalLoginPage from "@/pages/PortalLoginPage";
import TerritoryBuilderPage from "@/pages/TerritoryBuilderPage";
import CqcDefinitionsPage from "@/pages/CqcDefinitionsPage";
import CalendarPage from "@/pages/CalendarPage";
import PortalDashboardPage from "@/pages/PortalDashboardPage";
import BankingPage from "@/pages/BankingPage";
import OrdersPage from "@/pages/OrdersPage";
import OrderDetailPage from "@/pages/OrderDetailPage";
import FindClassAdminPage from "@/pages/FindClassAdminPage";
import AdminUsersPage from "@/pages/AdminUsersPage";
import AdminWebsiteProfileAuditPage from "@/pages/admin/AdminWebsiteProfileAuditPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import XeroSettingsPage from "@/pages/XeroSettingsPage";
import OrdersReconciliationPage from "@/pages/OrdersReconciliationPage";
import ScotlandDefinitionsPage from "@/pages/ScotlandDefinitionsPage";
import NiDefinitionsPage from "@/pages/NiDefinitionsPage";
import WalesDefinitionsPage from "@/pages/WalesDefinitionsPage";
import ProjectCodesAdminPage from "@/pages/ProjectCodesAdminPage";
import AdminHelpCentrePage from "@/pages/AdminHelpCentrePage";
import AdminSubscriptionRequestsPage from "@/pages/AdminSubscriptionRequestsPage";
import EmailTemplatesPage from "@/pages/EmailTemplatesPage";
import AdminMarketingTemplatesPage from "@/pages/AdminMarketingTemplatesPage";
import AnnouncementsPage from "@/pages/AnnouncementsPage";
import LogsPage from "@/pages/LogsPage";
import HubUsageReportPage from "@/pages/HubUsageReportPage";
import InfoLandingPage from "@/pages/InfoLandingPage";
import DiscoveryPortalPage from "@/pages/DiscoveryPortalPage";
import AdminLandingPagesPage from "@/pages/AdminLandingPagesPage";
import AdminInboundUnmatchedPage from "@/pages/AdminInboundUnmatchedPage";
import AdminAuditsPage from "@/pages/AdminAuditsPage";
import AdminFilesDiagPage from "@/pages/AdminFilesDiagPage";
import AdminFranchiseeDuplicatesPage from "@/pages/AdminFranchiseeDuplicatesPage";
// Invoices module (merged from Pay-Paperwork)
import InvoiceList from "@/pages/invoices/InvoiceList";
import CreateInvoice from "@/pages/invoices/CreateInvoice";
import EditInvoice from "@/pages/invoices/EditInvoice";
import InvoiceDetail from "@/pages/invoices/InvoiceDetail";
import InvoiceClients from "@/pages/invoices/InvoiceClients";
import DeletedInvoices from "@/pages/invoices/DeletedInvoices";
import InvoiceSettings from "@/pages/invoices/InvoiceSettings";
import InvoicesShell from "@/pages/invoices/InvoicesShell";
import ReconcilePage from "@/pages/invoices/ReconcilePage";
// Portal shell + section pages (refactored from PortalDashboardPage —
// each section is now its own route under /portal/* for a cleaner UX).
import PortalShell from "@/pages/portal/PortalShell";
import PortalDetailsPage from "@/pages/portal/PortalDetailsPage";
import PortalMarketingPage from "@/pages/portal/PortalMarketingPage";
import PortalMarketingSettingsPage from "@/pages/portal/PortalMarketingSettingsPage";
import PortalTrainingPage from "@/pages/portal/PortalTrainingPage";
import PortalPlaylistPage from "@/pages/portal/PortalPlaylistPage";
import PortalBookingsPage from "@/pages/portal/PortalBookingsPage";
import PortalShapeOrdersPage from "@/pages/portal/PortalShapeOrdersPage";
import AdminShapeOrdersPage from "@/pages/AdminShapeOrdersPage";
import AdminContractTemplatesPage from "@/pages/AdminContractTemplatesPage";
import AdminContractTemplateDetailPage from "@/pages/AdminContractTemplateDetailPage";
import AdminContractsPage from "@/pages/AdminContractsPage";
import AdminMarkerLibraryPage from "@/pages/AdminMarkerLibraryPage";
import PortalChangePasswordPage from "@/pages/portal/PortalChangePasswordPage";
import PortalSubscriptionsPage from "@/pages/portal/PortalSubscriptionsPage";
import AdminYouTubePage from "@/pages/AdminYouTubePage";
import PortalTerritoryPage from "@/pages/portal/PortalTerritoryPage";
import PortalEventsPage from "@/pages/portal/PortalEventsPage";
import PortalFilesPage from "@/pages/portal/PortalFilesPage";
import PortalPostersPage from "@/pages/portal/PortalPostersPage";
import PortalPosterEditorPage from "@/pages/portal/PortalPosterEditorPage";
import AdminPosterTemplatesPage from "@/pages/AdminPosterTemplatesPage";
import PortalUpdatesPage from "@/pages/portal/PortalUpdatesPage";
import PortalContractsPage from "@/pages/portal/PortalContractsPage";
// Portal invoices module — clone of admin Sandra's Invoices, scoped to franchisee.
import PortalInvoicesShell from "@/pages/portal/invoices/PortalInvoicesShell";
import PortalInvoiceList from "@/pages/portal/invoices/PortalInvoiceList";
import PortalInvoiceDetail from "@/pages/portal/invoices/PortalInvoiceDetail";
import CreatePortalInvoice from "@/pages/portal/invoices/CreatePortalInvoice";
import EditPortalInvoice from "@/pages/portal/invoices/EditPortalInvoice";
import PortalInvoiceClients from "@/pages/portal/invoices/PortalInvoiceClients";
import DeletedPortalInvoices from "@/pages/portal/invoices/DeletedPortalInvoices";
import PortalInvoiceSettings from "@/pages/portal/invoices/PortalInvoiceSettings";
import PortalReconcile from "@/pages/portal/invoices/PortalReconcile";

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          {/* EnvBanner floating pill was retired in favour of the
              SidebarVersionPill (bottom of Layout / PortalShell) so
              the deploy indicator no longer overlaps the Logout
              button. Removed 03 Jul 2026. */}
          {/* Top-level ErrorBoundary — without this any unhandled render
              exception in any descendant unmounts the entire tree and
              shows a blank white screen (instead of a recoverable error
              page). Originally added to diagnose Helen Bell's account-
              specific post-login crash on production. */}
          <ErrorBoundary>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/portal/login" element={<PortalLoginPage />} />
            <Route path="/change-password" element={
              <ProtectedRoute><ChangePasswordPage /></ProtectedRoute>
            } />
            <Route path="/portal" element={
              <ProtectedRoute role="franchisee"><PortalShell /></ProtectedRoute>
            }>
              <Route index element={<PortalDetailsPage />} />
              <Route path="details" element={<PortalDetailsPage />} />
              <Route path="territory" element={<PortalTerritoryPage />} />
              <Route path="territory/basic" element={<PortalTerritoryPage forceBasic={true} />} />
              <Route path="events" element={<PortalEventsPage />} />
              {/* Legacy alias — older handover emails / docs occasionally
                  referred to /portal/calendar. Redirect to canonical
                  /portal/events so the link doesn't 404. */}
              <Route path="calendar" element={<Navigate to="/portal/events" replace />} />
              <Route path="marketing" element={<PortalMarketingPage />} />
              <Route path="marketing/settings" element={<PortalMarketingSettingsPage />} />
              <Route path="training" element={<PortalTrainingPage />} />
              <Route path="training/:playlistId" element={<PortalPlaylistPage />} />
              <Route path="bookings" element={<PortalBookingsPage />} />
              <Route path="shape-orders" element={<PortalShapeOrdersPage />} />
              <Route path="account/password" element={<PortalChangePasswordPage />} />
              <Route path="account/subscriptions" element={<PortalSubscriptionsPage />} />
              <Route path="files" element={<PortalFilesPage />} />
              <Route path="posters" element={<PortalPostersPage />} />
              <Route path="posters/new/:templateId" element={<PortalPosterEditorPage />} />
              <Route path="posters/:posterId" element={<PortalPosterEditorPage />} />
              <Route path="updates" element={<PortalUpdatesPage />} />
              <Route path="contracts" element={<PortalContractsPage />} />
              <Route path="invoices" element={<PortalInvoicesShell />}>
                <Route index element={<PortalInvoiceList />} />
                <Route path="reconcile" element={<PortalReconcile />} />
                <Route path="new" element={<CreatePortalInvoice />} />
                <Route path="deleted" element={<DeletedPortalInvoices />} />
                <Route path="clients" element={<PortalInvoiceClients />} />
                <Route path="settings" element={<PortalInvoiceSettings />} />
                <Route path=":id" element={<PortalInvoiceDetail />} />
                <Route path=":id/edit" element={<EditPortalInvoice />} />
              </Route>
            </Route>
            <Route path="/share/folder/:token" element={<PublicFolderSharePage />} />
            <Route path="/dbs/apply/:token" element={<PublicDBSForm />} />
            <Route path="/share/territory/:token" element={<PublicTerritorySharePage />} />
            {/* Public agreed-territory viewer — the URL baked into every
                issued contract PDF as [[TERRITORY_MAP_URL]]. */}
            <Route path="/agreed-territory/:snapshotId/:token" element={<PublicAgreedTerritoryPage />} />
            {/* Public PDF landing pages — no auth required so recipients can
                view from any inbox / forwarded email. */}
            <Route path="/info/:slug" element={<InfoLandingPage />} />
            {/* Discovery Portal (V1) — token-gated public page. The
                trailing slash + query-string form ``/discovery/?t=…``
                keeps the URL opaque and reuses the landing-pages
                public-page pattern (no login). Also register the
                slash-less variant so admins pasting either shape
                land on the same page. */}
            <Route path="/discovery" element={<DiscoveryPortalPage />} />
            <Route path="/discovery/" element={<DiscoveryPortalPage />} />
            <Route path="/discovery/:slug" element={<DiscoveryPortalPage />} />
            <Route
              element={
                <ProtectedRoute role="admin">
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<DashboardPage />} />
              <Route path="/franchisees" element={<FranchiseesPage />} />
              <Route path="/franchisees/:id" element={<FranchiseeDetailPage />} />
              <Route path="/contracts" element={<ContractsPage />} />
              <Route path="/renewals" element={<ContractRenewalsPage />} />
              <Route path="/files" element={<FilesPage />} />
              <Route path="/territory-builder" element={<TerritoryBuilderPage />} />
              <Route path="/cqc-definitions" element={<CqcDefinitionsPage />} />
              <Route path="/scotland-definitions" element={<ScotlandDefinitionsPage />} />
              <Route path="/ni-definitions" element={<NiDefinitionsPage />} />
              <Route path="/wales-definitions" element={<WalesDefinitionsPage />} />
              <Route path="/admin/project-codes" element={<ProjectCodesAdminPage />} />
              <Route path="/admin/help-centre" element={<AdminHelpCentrePage />} />
              <Route path="/admin/subscription-requests" element={<AdminSubscriptionRequestsPage />} />
              <Route path="/admin/email-templates" element={<EmailTemplatesPage />} />
              <Route path="/admin/marketing-templates" element={<AdminMarketingTemplatesPage />} />
              <Route path="/admin/landing-pages" element={<AdminLandingPagesPage />} />
              <Route path="/admin/inbound-unmatched" element={<AdminInboundUnmatchedPage />} />
              <Route path="/admin/audits" element={<AdminAuditsPage />} />
              <Route path="/admin/files-diag" element={<AdminFilesDiagPage />} />
              <Route path="/admin/franchisee-duplicates" element={<AdminFranchiseeDuplicatesPage />} />
              <Route path="/admin/announcements" element={<AnnouncementsPage />} />
              <Route path="/admin/logs" element={<LogsPage />} />
              <Route path="/admin/usage" element={<HubUsageReportPage />} />
              <Route path="/admin/youtube" element={<AdminYouTubePage />} />
              <Route path="/calendar" element={<CalendarPage />} />
              <Route path="/contacts" element={<ContactsPage />} />
              <Route path="/form-intake" element={<FormIntakePage />} />
              {/* Invoices module — merged from the legacy Pay-Paperwork app */}
              <Route path="/invoices" element={<InvoicesShell />}>
                <Route index element={<InvoiceList />} />
                <Route path="reconcile" element={<ReconcilePage />} />
                <Route path="new" element={<CreateInvoice />} />
                <Route path="deleted" element={<DeletedInvoices />} />
                <Route path="clients" element={<InvoiceClients />} />
                <Route path="settings" element={<InvoiceSettings />} />
                <Route path=":id" element={<InvoiceDetail />} />
                <Route path=":id/edit" element={<EditInvoice />} />
              </Route>
              <Route path="/banking" element={<BankingPage />} />
              <Route path="/orders" element={<OrdersPage />} />
              <Route path="/orders/reconcile" element={<OrdersReconciliationPage />} />
              <Route path="/orders/:orderId" element={<OrderDetailPage />} />
              <Route path="/find-class" element={<FindClassAdminPage />} />
              <Route path="/admin/users" element={<AdminUsersPage />} />
              <Route path="/admin/website-profile-audit" element={<AdminWebsiteProfileAuditPage />} />
              <Route path="/admin/shape-orders" element={<AdminShapeOrdersPage />} />
              <Route path="/admin/contracts/templates" element={<AdminContractTemplatesPage />} />
              <Route path="/admin/poster-templates" element={<AdminPosterTemplatesPage />} />
              <Route path="/admin/contracts/templates/:id" element={<AdminContractTemplateDetailPage />} />
              <Route path="/admin/contracts" element={<AdminContractsPage />} />
              <Route path="/admin/markers-library" element={<AdminMarkerLibraryPage />} />
              <Route path="/admin/password-resets" element={<AdminUsersPage />} />
              <Route path="/admin/xero" element={<XeroSettingsPage />} />
            </Route>
          </Routes>
          </ErrorBoundary>
        </BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </div>
  );
}
